import type {
  FunctionComponent,
  KeyboardEventHandler,
  MouseEventHandler,
} from 'react';
import { useCallback } from 'react';

import ArchiveBoxIcon from '@atlaskit/icon/core/archive-box';
import { Analytics } from '@trello/atlassian-analytics';
import { intl } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { Tooltip } from '@trello/nachos/tooltip';

import { useArchiveCard } from './useArchiveCard';

import styles from './CardFrontArchiveButton.less';

export const CardFrontArchiveButton: FunctionComponent = () => {
  const archiveCard = useArchiveCard();

  const handleAction = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'cardFrontArchiveButton',
      source: 'cardView',
    });

    archiveCard();
  }, [archiveCard]);

  const onClick = useCallback<MouseEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleAction();
    },
    [handleAction],
  );

  const handleKeyDown = useCallback<KeyboardEventHandler<HTMLButtonElement>>(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        handleAction();
      }
    },
    [handleAction],
  );

  return (
    <Tooltip
      content={intl.formatMessage({
        id: 'templates.card_front.archive-card',
        defaultMessage: 'Archive card',
        description: 'Tooltip content for the archive card button',
      })}
    >
      <Button
        aria-label={intl.formatMessage({
          id: 'templates.card_front.archive-card',
          defaultMessage: 'Archive card',
          description: 'Tooltip content for the archive card button',
        })}
        className={styles.archiveButton}
        onClick={onClick}
        iconBefore={
          <ArchiveBoxIcon
            label={intl.formatMessage({
              id: 'templates.card_front.archive-card',
              defaultMessage: 'Archive card',
              description: 'Tooltip content for the archive card button',
            })}
            color="currentColor"
          />
        }
        tabIndex={0}
        onKeyDown={handleKeyDown}
      />
    </Tooltip>
  );
};
